import React, { useState , useEffect } from "react";
import Header from "./Header";
import Footer from "./Footer";
import styled, { keyframes } from "styled-components";
import { useNavigate } from "react-router-dom";
import Euca from "./images/EUCALY.png";
import { useTranslation } from 'react-i18next';

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const Success = () => {
  const [stories, setStories] = useState([]);
  const [currentStoryIndex, setCurrentStoryIndex] = useState(0);
  const { t, i18n } = useTranslation();
  const navigate = useNavigate()

  useEffect(() => {
    const fetchStories = async () => {
      try {
          const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8000/";
        const response = await fetch(`${API_URL}getSuccessStories`);
        const data = await response.json();

        const localizedStories = data.map(story => ({
          ...story,
          quote: story.quote[i18n.language] || story.quote.en,
          summary: story.summary[i18n.language] || story.summary.en,
        }));

        setStories(localizedStories);
      } catch (error) {
        console.error("Failed to fetch success stories:", error);
      }
    };

    fetchStories();
  }, [i18n.language]);

  const handleNext = () => {
    setCurrentStoryIndex((prevIndex) => (prevIndex + 1) % stories.length);
  };

  const handlePrev = () => {
    setCurrentStoryIndex((prevIndex) => (prevIndex - 1 + stories.length) % stories.length);
  };

  if (stories.length === 0) {
    return (
      <SuccessPage className="eucalyptus">
        <Header />
        <p style={{ textAlign: "center", marginTop: "50px" }}>{t('success.Loading')}</p>
        <Footer />
      </SuccessPage>
    );
  }

  const { name, lost, images, quote, summary } = stories[currentStoryIndex];

  return (
    <SuccessPage>
      <Header />
      <div>
        <Title>
          <p>{t('success.Success')}</p>
        </Title>
        <Story>
          <Rightbox>
            <PersonInfo>
              <Name>{name}</Name>
              <Divider />
              <Lost>{t('success.Lost')} {lost} {t('success.Pounds')}</Lost>
            </PersonInfo>
            <ImageRow>
              <Polaroid rotate="-8deg">
                <P>
                  <IMG src={images[0]} alt={`${name} first`} />
                </P>
              </Polaroid>
              <Polaroid rotate="8deg">
                <P>
                  <IMG src={images[1]} alt={`${name} second`} />
                </P>
              </Polaroid>
            </ImageRow>
            <ButtonWrapper>
              <p>{t('success.Inspired')}</p>
              <button onClick={() => navigate("/consultation")}>{t('success.Start')}</button>
            </ButtonWrapper>
          </Rightbox>
          <DivQuoteMid>
            <Quotediv>
              <p>{quote}</p>
            </Quotediv>
            <UnderPics>
              <p>{summary}</p>
            </UnderPics>
          </DivQuoteMid>
        </Story>
        <Navigation>
          <button onClick={handlePrev}>{t('success.Previous')}</button>
          <button onClick={handleNext}>{t('success.Next')}</button>
        </Navigation>
      </div>
      <Footer />
    </SuccessPage>
  );
};

export default Success;

const Rightbox = styled.div`
  border-radius: 10px;
  max-width: 40%;
  min-width: 350px;
  height: 650px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  @media (max-width: 1024px) {
    max-width: 90%;
    min-width: unset;
    height: auto;
  }
`;

const Title = styled.div`
  text-align: center;
  font-family: 'Great Vibes', cursive;
  font-size: 50px;
  padding: 10px;
  padding-top: 20px;
  padding-bottom: 30px;

  @media (max-width: 768px) {
    font-size: 36px;
    padding-top: 60px;
  }

  @media (max-width: 480px) {
    font-size: 30px;
    padding-top: 40px;
  }
`;

const PersonInfo = styled.div`
  background-color: rgba(186, 204, 171, 0.4);
  border-radius: 10px;
  text-align: center;
  width: 100%;
  font-size: 25px;
  padding-top: 10px;

  @media (max-width: 768px) {
    font-size: 20px;
  }
`;

const Name = styled.p`
  margin-bottom: -30px;
  margin-top: -2px;
  font-weight: bolder;
  font-size: 40px;
  text-shadow: 0 2px 12px white;
  font-family: 'Great Vibes', cursive;

  @media (max-width: 768px) {
    font-size: 30px;
  }
`;

const Lost = styled.p`
  margin-top: -2px;
  padding-bottom: 10px;
  font-weight: bold;
  font-style: italic;
  color: rgb(233, 149, 129);
  text-shadow: 0 2px 12px black;

  @media (max-width: 768px) {
    font-size: 20px;
  }
`;

const ImageRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  margin-top: 20px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
    gap: 20px;
  }
`;

const Polaroid = styled.div`
  background-color: white;
  padding-bottom: 40px;
  width: 250px;
  box-shadow: 0px 4px 20px rgba(246, 187, 155, 1);
  text-align: center;
  margin-left: 20px;
  transform: rotate(${(props) => props.rotate || "0deg"});
  transition: transform 0.3s ease;

  &:hover {
    box-shadow: 0px 4px 20px rgba(186, 204, 171, 1);
    transform: rotate(${(props) => props.rotate || "0deg"}) scale(1.3);
  }

  @media (max-width: 1400px) {
    margin-left: 0;
    width: 200px;
  }
`;

const IMG = styled.img`
  width: 90%;
  object-fit: cover;
  max-height: 400px;
`;

const P = styled.p`
  background-color: white;
`;

const Divider = styled.hr`
  width: 100%;
  border: none;
  border-top: 2px dashed white;
  margin: 30px 0 10px;
`;

const Quotediv = styled.div`
  background-color: rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 40px;
  font-size: 24px;
  text-align: center;
  border-radius: 10px;
  position: relative;
  font-style: italic;
  line-height: 1.5;
  animation: ${fadeIn} 1.5s ease forwards;
  overflow: hidden;

  &::before,
  &::after {
    content: '"';
    font-size: 80px;
    color: black;
    position: absolute;
    font-family: serif;
  }

  &::before {
    top: 10px;
    left: 20px;
  }

  &::after {
    bottom: 10px;
    right: 20px;
  }

  p {
    margin: 0;
    max-width: 500px;
    padding: 10px;
  }
  
`;


const UnderPics = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  padding: 20px;
  margin-top: 20px;
  font-size: 18px;
  text-align: center;

  @media (max-width: 1000px) {
    font-size: 16px;
  }

  @media (max-width: 480px) {
    max-height: 30vh;
    overflow-y: auto;
    padding: 10px;
    margin-top: 10px;
    font-size: 16px;
  }
`;


const Story = styled.div`
  animation: ${fadeIn} 1s ease-in-out;
   font-family: 'Montserrat', sans-serif;
  font-weight: 400;
  display: flex;
  flex-direction: row;
  justify-content: center;
  gap: 40px;
  background: linear-gradient(
    122deg,
    rgba(148, 163, 135, 1.1) 0%,
    rgba(186, 204, 171, 1.1) 43%,
    rgba(246, 187, 155, 1.1) 83%,
    rgba(217, 121, 97, 1.1) 100%
  );
  box-shadow: 0 8px 24px black;
  padding: 40px;
  border-radius: 10px;
  min-height: 700px;
  height: auto;
  margin: 0% 10%;
  overflow: hidden;

  @media (max-width: 1000px) {
    flex-direction: column;
    align-items: center;
    margin: 20px;
    padding: 20px;
    min-height: auto;
  }
`;


const DivQuoteMid = styled.div`
  display: flex;
  flex-direction: column;
  width: 55%;
  height: 700px;
  overflow: hidden;

  @media (max-width: 1024px) {
    height: auto;
    width: 99%;
  }
`;



const ButtonWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 4%;

  button {
    padding: 10px 20px;
    background-color: rgba(233, 149, 129, 1);
    color: white;
     font-family: 'Montserrat', sans-serif;
  font-weight: 500;
    font-size: 16px;
    font-weight: bold;
    border: none;
    border-radius: 5px;
    cursor: pointer;

    &:hover {
      background-color: rgba(148, 163, 135, 1);
    }
  }

  p {
    font-size: 18px;
    margin-bottom: 10px;
  }

  @media (max-width: 480px) {
    button {
      font-size: 14px;
      padding: 8px 16px;
    }

    p {
      font-size: 16px;
    }
  }
`;

const Navigation = styled.div`
  display: flex;
  justify-content: center;
  margin: 30px 0;
  margin-bottom: 50px;

  button {
    background-color: rgba(233, 149, 129, 1); 
    color: white;
    font-weight: bold;
    font-size: 1.1rem; 
    border: none;
    padding: 14px 28px; 
    margin: 0 20px;
    border-radius: 8px;
    cursor: pointer;
    box-shadow: 0 4px 8px rgba(0,0,0,0.1); 
    transition: background-color 0.3s, transform 0.2s;

    &:hover {
      background-color: rgba(100, 135, 70, 1);
      transform: scale(1.05);
    }

    &:focus {
      outline: 3px solid rgba(100, 135, 70, 0.6);
    }
  }

  @media (max-width: 768px) {
    button {
      font-size: 1rem;
      padding: 10px 20px;
      margin: 0 10px;
    }
  }

  @media (max-width: 480px) {
    flex-direction: column;
    gap: 10px;

    button {
      font-size: 0.95rem;
      padding: 8px 16px;
    }
  }
`;

const SuccessPage = styled.div`
  background-color: #f7f9fc;
  background-image: url(${Euca});
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
`;

